import { ApiError } from '@gorila-shared-ui/components';
import { ApiEndpoint } from '../types/api';
import { Device, GpsType } from '../types/device';
import { DeviceBrand } from '../types/deviceBrand';
import { DeviceCarrier } from '../types/deviceCarrier';
import { DeviceModel } from '../types/deviceModel';
import { getRequest, postRequest } from './api';
import { URLS } from './urls/urls';

// Devices
export const getDevicesList = async (
  page?: number,
  q?: string,
  gpsType?: GpsType,
): Promise<{ devices?: Device[]; error?: ApiError; total: number; hasNext: boolean }> => {
  const params = {
    page: page ?? 1,
    q,
    gpsType,
  };
  const response = await getRequest(URLS.devices.all, {
    queryParams: params,
  });
  return {
    devices: response?.data?.items,
    total: response?.data?.total ?? 0,
    hasNext: response?.data?.hasNext ?? false,
    error: response?.error,
  };
};

export const getDevice = async (deviceId: string): Promise<{ device?: Device; error?: ApiError }> => {
  const response = await getRequest(URLS.devices.detail(deviceId), {});
  return {
    device: response?.data,
    error: response?.error,
  };
};

export const postDevice = async (device: Device): Promise<{ id?: string; error?: ApiError }> => {
  const body = {
    brandId: device.brand._id,
    modelId: device.model._id,
    ident: device.ident,
    carrier: device.carrier,
    imei: device.imei,
    sim: device.sim,
    maxMb: device.maxMb,
    gpsType: device.gpsType,
  };
  const response = await postRequest(URLS.devices.create, {
    body,
  });
  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

export const updateDevice = async (device: Device): Promise<{ id?: string; error?: ApiError }> => {
  const body = {
    brandId: device.brand._id,
    modelId: device.model._id,
    ident: device.ident,
    carrier: device.carrier,
    imei: device.imei,
    sim: device.sim,
    maxMb: device.maxMb,
    gpsType: device.gpsType,
    channels: device.channels,
  };
  const response = await postRequest(URLS.devices.update(device._id), {
    body,
  });
  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

// Devices models
export const getDeviceModelsList = async (
  page?: number,
  q?: string,
  deviceBrandId?: string,
): Promise<{ models?: DeviceModel[]; error?: ApiError; total: number; hasNext: boolean }> => {
  const params = {
    page: page ?? 1,
    q,
    deviceBrandId,
  };
  const response = await getRequest(
    URLS.devices.modelList,
    {
      queryParams: params,
    },
    ApiEndpoint.configuration,
  );
  return {
    models: response?.data?.items,
    total: response?.data?.total ?? 0,
    hasNext: response?.data?.hasNext ?? false,
    error: response?.error,
  };
};

export const postDevicesModel = async (deviceModel: DeviceModel): Promise<{ id?: string; error?: ApiError }> => {
  const body = {
    name: deviceModel.name,
    brandId: deviceModel.brand._id,
  };
  const response = await postRequest(
    URLS.devices.createModel,
    {
      body,
    },
    ApiEndpoint.configuration,
  );
  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

// Device brands
export const getDeviceBrandsList = async (
  page?: number,
  q?: string,
): Promise<{ brands?: DeviceBrand[]; error?: ApiError; total: number; hasNext: boolean }> => {
  const params = {
    page: page ?? 1,
    q,
  };
  const response = await getRequest(
    URLS.devices.brandList,
    {
      queryParams: params,
    },
    ApiEndpoint.configuration,
  );
  return {
    brands: response?.data?.items,
    total: response?.data?.total ?? 0,
    hasNext: response?.data?.hasNext ?? false,
    error: response?.error,
  };
};

export const postDevicesBrand = async (deviceBrand: DeviceBrand): Promise<{ id?: string; error?: ApiError }> => {
  const body = {
    name: deviceBrand.name,
  };
  const response = await postRequest(
    URLS.devices.createBrand,
    {
      body,
    },
    ApiEndpoint.configuration,
  );
  return {
    id: response?.data?.id,
    error: response?.error,
  };
};

// Device carriers
export const getDeviceCarriersList = async (): Promise<{ carriers?: DeviceCarrier[]; error?: ApiError }> => {
  const response = await getRequest(URLS.devices.carrierList, {});
  return {
    carriers: response?.data,
    error: response?.error,
  };
};
